"use client"

import type { AutoFormInputComponentProps, ExtendedSelectorItem } from "../types"
import type { Numberish } from "@/models/common.model"
import { Icon } from "@iconify/react"
import { X } from "lucide-react"
import { useTranslations } from "next-intl"
import { useCallback, useMemo, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { FormControl, FormItem, FormMessage } from "@/components/ui/form"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import AutoFormLabel from "../common/label"
import AutoFormTooltip from "../common/tooltip"

export function AutoFormEnum({
  label,
  isRequired,
  field,
  fieldConfigItem,
  fieldProps,
}: AutoFormInputComponentProps) {
  const { options, showSelectAll } = fieldConfigItem.inputProps || {}
  const [open, setOpen] = useState(false)
  const t = useTranslations("generic")
  const isMultiple = fieldProps?.multiple
  const min = fieldProps?.min
  const max = fieldProps?.max
  const selectedValues = useMemo(() => isMultiple ? (field.value as Numberish[] || []) : [field.value].filter(Boolean), [field.value, isMultiple])

  const filteredFieldProps = useMemo(() => {
    // the button doesnt need to know if the options are multiple or not
    const { multiple, ...rest } = fieldProps
    return rest
  }, [fieldProps])

  const handleSelect = (optionValue: Numberish) => {
    if (isMultiple) {
      const currentValues = (field.value as Numberish[]) || []
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v: Numberish) => v !== optionValue)
        : [...currentValues, optionValue]

      // Check if we're removing an item and if it would violate min constraint
      if (currentValues.includes(optionValue) && min && newValues.length < Number(min)) {
        return
      }

      // Check if we're adding an item and if it would violate max constraint
      if (!currentValues.includes(optionValue) && max && newValues.length > Number(max)) {
        return
      }

      field.onChange(newValues)
    }
    else {
      field.onChange(field.value === optionValue ? null : optionValue)
      // only close the popover when its a single selector
      setOpen(false)
    }
  }

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newValue = isMultiple ? [] : null
    // Only allow clearing if we're not violating min constraint
    if (!min || (newValue?.length ?? 0) >= Number(min)) {
      field.onChange(newValue)
    }
  }

  const selectAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isMultiple && options) {
      const availableOptions = options.filter(option => !option.disabled && !option.isHeader)
      const allValues = availableOptions.map(option => option.value)
      field.onChange(allValues)
    }
  }

  const constraintsText = useMemo(() => {
    if (!isMultiple)
      return ""
    const current = selectedValues.length
    const remaining = max ? Number(max) - current : null
    const minText = min ? `Min: ${String(min)}` : ""
    const maxText = max ? `Max: ${String(max)}` : ""
    const remainingText = remaining !== null ? `(Remaining: ${String(remaining)})` : ""

    return [minText, maxText, remainingText].filter(Boolean).join(" • ")
  }, [isMultiple, selectedValues, min, max])

  const shouldDisableItem = useCallback((optionValue: Numberish, option: ExtendedSelectorItem) => {
    // Si la opción está marcada como disabled, deshabilitar
    if (option?.disabled)
      return true
    if (!isMultiple)
      return false
    if (selectedValues.includes(optionValue))
      return false
    if (max && selectedValues.length >= Number(max))
      return true
    return false
  }, [isMultiple, selectedValues, max])

  return (
    <FormItem className={cn(fieldConfigItem?.containerClassName)}>
      <AutoFormLabel
        isRequired={isRequired}
        label={fieldConfigItem?.label || label}
      />
      <Popover
        modal={true}
        onOpenChange={setOpen}
        open={open}
      >
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              role="combobox"
              variant="outline"
              {...filteredFieldProps}
              className={cn(
                "w-full justify-between",
                !selectedValues.length && "text-muted-foreground",
              )}
            >
              {selectedValues.length > 0
                ? (
                    <div className="flex max-w-full flex-wrap gap-1 overflow-x-auto">
                      {selectedValues.slice(0, 2).map((value: string | number) => (
                        <Badge
                          key={String(value)}
                          className="mr-1 max-w-[120px]"
                          variant="secondary"
                        >
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {options?.find(option => option.value === value)?.label}
                          </span>
                        </Badge>
                      ))}
                      {selectedValues.length > 2 && (
                        <span className="text-muted-foreground">
                          {t("more", { count: selectedValues.length - 2 })}
                        </span>
                      )}
                    </div>
                  )
                : (
                    <span className="text-muted-foreground">{t("select")}</span>
                  )}
              <div className="ml-2 flex shrink-0 items-center">
                {selectedValues.length > 0 && (
                  <div
                    className="flex cursor-pointer items-center justify-center rounded-full p-1 text-destructive transition-colors hover:bg-destructive/20"
                    onClick={clearSelection}
                  >
                    <X className="size-4 shrink-0" />
                  </div>
                )}
                {showSelectAll && isMultiple
                  ? (
                      <div
                        className="flex cursor-pointer items-center justify-center rounded-full p-1 text-primary transition-colors hover:bg-primary/20"
                        onClick={selectAll}
                        title="Seleccionar todos"
                      >
                        <Icon className="size-4 shrink-0" icon="lucide:check-all" />
                      </div>
                    )
                  : null}
                <Icon className="ml-2 size-4 shrink-0 opacity-50" icon="lucide:chevron-down" />
              </div>
            </Button>
          </FormControl>
        </PopoverTrigger>
        {constraintsText && (
          <p className="mt-1 text-sm text-muted-foreground">
            {constraintsText}
          </p>
        )}
        <PopoverContent className="w-full p-0 md:max-w-[300px] md:min-w-[250px]">
          <Command>
            <CommandInput
              className="h-9"
              placeholder={fieldConfigItem.inputProps?.placeholder}
            />
            <CommandList>
              <CommandEmpty>{t("not_found")}</CommandEmpty>
              <CommandGroup>
                {showSelectAll && isMultiple
                  ? (
                      <CommandItem
                        className="cursor-pointer border-b bg-muted/30"
                        onSelect={() => selectAll({ stopPropagation: () => {} } as React.MouseEvent)}
                      >
                        <Icon className="mr-2" icon="lucide:check-all" />
                        {t("select_all")}
                      </CommandItem>
                    )
                  : null}
                {options?.map(option => (
                  <CommandItem
                    key={String(option.value)}
                    className={cn(
                      "overflow-hidden text-ellipsis whitespace-nowrap",
                      shouldDisableItem(option.value, option)
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer",
                      option.isHeader && "font-semibold text-muted-foreground bg-muted/50 cursor-default",
                      option.disabled && !option.isHeader && "text-muted-foreground/50",
                    )}
                    disabled={shouldDisableItem(option.value, option)}
                    onSelect={() => !shouldDisableItem(option.value, option) && handleSelect(option.value)}
                    value={String(option.label)}
                  >
                    {option.label}
                    {!option.disabled && !option.isHeader && (
                      <Icon
                        className={cn(
                          "ml-auto",
                          selectedValues.includes(option.value)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                        icon="lucide:check"
                      />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <AutoFormTooltip fieldConfigItem={fieldConfigItem} />
      <FormMessage />
    </FormItem>
  )
}

export default AutoFormEnum
